import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { Field, NinoInput } from '@billda/design-system';

/**
 * Converts a displayed NINO value (with spaces) to raw format (no spaces).
 * @example ninoToRaw('PE 63 54 34 C') // 'PE635434C'
 */
export const ninoToRaw = (displayValue: string): string =>
  displayValue.replace(/[\s-]/g, '').toUpperCase();

/**
 * Converts a raw NINO value to display format (with spaces).
 * @example ninoToDisplay('PE635434C') // 'PE 63 54 34 C'
 */
export const ninoToDisplay = (rawValue: string): string => {
  if (!rawValue) return '';
  const clean = rawValue.toUpperCase().replace(/[\s-]/g, '');
  if (clean.length !== 9) return rawValue;
  return `${clean.slice(0, 2)} ${clean.slice(2, 4)} ${clean.slice(4, 6)} ${clean.slice(6, 8)} ${clean.slice(8)}`;
};

export interface RHFNinoInputProps<
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined = undefined,
> {
  /** Field name - must match form schema */
  name: FieldPath<T>;
  /** React Hook Form control object */
  control: Control<T, any, TTransformedValues>;
  /** Field label */
  label?: string;
  /** Helper text to display below the input */
  helperText?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Full width of parent container */
  fullWidth?: boolean;
  /** Data test ID for testing */
  'data-testid'?: string;
}

/**
 * National Insurance Number input with masking, integrated with React Hook
 * Form (DS fork of shared-ui RHFNinoInput). Displays as "PE 63 54 34 C" but
 * stores the raw value "PE635434C" (the DS NinoInput mask unmasks on change).
 *
 * @example
 * ```tsx
 * <RHFNinoInput
 *   name="soleTrader.nationalInsuranceNumber"
 *   control={form.control}
 *   label="National Insurance Number"
 *   required
 * />
 * ```
 */
export const RHFNinoInput = <
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined = undefined,
>({
  name,
  control,
  label,
  helperText,
  disabled,
  required,
  fullWidth,
  'data-testid': testId,
}: RHFNinoInputProps<T, TTransformedValues>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, value, onChange, onBlur },
        fieldState: { error },
      }) => (
        <Field
          label={label}
          error={error?.message}
          hint={helperText}
          required={required}
          className={fullWidth ? 'w-full' : 'w-fit'}
        >
          <NinoInput
            value={ninoToRaw(value ?? '')}
            onChange={onChange}
            onBlur={onBlur}
            ref={ref}
            disabled={disabled}
            invalid={!!error}
            autoCapitalize="characters"
            className="uppercase"
            data-testid={testId}
          />
        </Field>
      )}
    />
  );
};
